/**
 * 宿主嵌入桥（iframe 模式，`?embed=1`）。
 *
 * 场景：宿主（Vue + Element Plus 报告系统）在 el-dialog 里 `<iframe>` 本编辑器，
 * 只需要两件事 —— **打开这份 SVG / 拿到改完的 SVG 存回去**，不需要任何双向联动。
 *
 * 设计纪律：
 *  · 消息一律带 `channel: EMBED_CHANNEL` 信封，宿主自己的 postMessage 流量不会被误判（反之亦然）
 *  · **编辑器不主动落盘**：宿主发 `get` 才回文本；未修改时回的是**原始字符串**（字节零变化）
 *  · 纯逻辑（createEmbedBridge）与窗口接线（attachEmbed）分开 —— 前者可在 jsdom 里直接断言
 *  · 默认只接受**同源**宿主（产物与宿主同域部署）；跨域部署时用 `?anyOrigin=1` 显式放开
 *
 * 协议全表见 `docs/EMBED.md`。
 */

/** 消息信封标识：宿主只认这个 channel */
export const EMBED_CHANNEL = 'svg-edit-lite';

/** 启用嵌入模式的查询参数（`?embed=1`） */
export const EMBED_PARAM = 'embed';

/** 不校验宿主来源的查询参数（`?anyOrigin=1`，产物跨域部署时才用） */
export const ANY_ORIGIN_PARAM = 'anyOrigin';

export interface EmbedEditorDeps {
  /** 当前文档文本；**未修改时返回原始字符串**（宿主的"没改就不落盘"靠它） */
  getSvgText(): string;
  /** 以新文档载入（复用 openText 的校验与状态提示）；返回是否成功 */
  loadSvgText(text: string, name: string | null): boolean;
  isDirty(): boolean;
  canUndo(): boolean;
  canRedo(): boolean;
  docName(): string | null;
}

export interface EmbedBridgeDeps extends EmbedEditorDeps {
  /** 发消息给宿主 */
  post(message: Record<string, unknown>, targetOrigin: string): void;
  /** 编辑器自身 origin（同源校验用） */
  origin(): string;
}

export interface EmbedBridgeOptions {
  /** 发消息的目标 origin（同源部署 = 编辑器自己的 origin） */
  targetOrigin: string;
  /** true = 接受任意来源的宿主消息（默认只接受 targetOrigin） */
  anyOrigin?: boolean;
}

export interface EmbedBridge {
  /** 处理一条宿主消息；返回 true = 已消费（未被消费的消息宿主别指望有回应） */
  handleMessage(data: unknown, origin: string): boolean;
  /** 编辑器 → 宿主：已就绪（**宿主收到它之后**才该发 load） */
  notifyReady(): void;
  /** 编辑器 → 宿主：脏状态变化（宿主据此启用/禁用「确定」） */
  notifyChange(): void;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** 把未知值读成字符串（缺省空串） */
const str = (value: unknown): string => (typeof value === 'string' ? value : '');

export function createEmbedBridge(deps: EmbedBridgeDeps, options: EmbedBridgeOptions): EmbedBridge {
  const targetOrigin = options.targetOrigin;
  const accepted = (origin: string): boolean => options.anyOrigin === true || origin === targetOrigin;

  const send = (message: Record<string, unknown>): void => {
    deps.post({ channel: EMBED_CHANNEL, ...message }, targetOrigin);
  };

  /** 宿主最关心的那几项状态，统一形状（ready / change / doc 都带） */
  const state = (): Record<string, unknown> => ({
    dirty: deps.isDirty(),
    canUndo: deps.canUndo(),
    canRedo: deps.canRedo(),
    name: deps.docName(),
  });

  const notifyChange = (): void => {
    send({ type: 'change', ...state() });
  };

  const notifyReady = (): void => {
    send({ type: 'ready', ...state() });
  };

  return {
    handleMessage(data: unknown, origin: string): boolean {
      if (!accepted(origin)) return false;
      if (!isRecord(data) || data.channel !== EMBED_CHANNEL) return false;
      const type = str(data.type);

      if (type === 'load') {
        const svg = str(data.svg);
        const name = typeof data.name === 'string' ? data.name : null;
        if (svg.trim() === '') {
          send({ type: 'loaded', ok: false, error: '宿主没有传 svg 文本' });
          return true;
        }
        const ok = deps.loadSvgText(svg, name);
        send({ type: 'loaded', ok, ...(ok ? {} : { error: 'SVG 解析失败（标签未闭合 / 不是 SVG 文档）' }) });
        // 换文档后脏状态/文档名都变了，主动推一次（宿主不必再发 get）
        notifyChange();
        return true;
      }

      if (type === 'get') {
        send({
          type: 'doc',
          svg: deps.getSvgText(),
          ...state(),
          ...(typeof data.requestId === 'string' ? { requestId: data.requestId } : {}),
        });
        return true;
      }

      // 宿主若不确定编辑器是否已就绪（怕漏掉 ready），可发 ping 探测
      if (type === 'ping') {
        notifyReady();
        return true;
      }

      return false;
    },

    notifyReady,

    notifyChange,
  };
}

/* ------------------------------------------------------------------ 查询参数 */

export interface EmbedOptions {
  /** `?embed=1` → 启用宿主消息桥 */
  enabled: boolean;
  /** `?anyOrigin=1` → 不校验宿主来源 */
  anyOrigin: boolean;
}

/** 解析 URL 查询串（传 `window.location.search`）；`embed=0` / `embed=false` 视为未启用 */
export function readEmbedOptions(search: string): EmbedOptions {
  const params = new URLSearchParams(search);
  const embed = params.get(EMBED_PARAM);
  const anyOrigin = params.get(ANY_ORIGIN_PARAM);
  return {
    enabled: embed !== null && embed !== '0' && embed.toLowerCase() !== 'false',
    anyOrigin: anyOrigin !== null && anyOrigin !== '0' && anyOrigin.toLowerCase() !== 'false',
  };
}

/** 是否真的被嵌在 iframe 里（顶层打开时 `window.parent === window`，桥不起作用） */
export function isEmbedded(win: { parent: unknown } = window): boolean {
  return win.parent !== win;
}

/**
 * 嵌入会话判定：**确实在 iframe 里** 且 URL 带 `?embed=1`。
 * 用于关掉与"宿主轮流编辑不同文件"这件事冲突的功能 —— 目前只有一项：自动草稿。
 *
 * ⚠️ 为什么必须关草稿：宿主是同一个弹窗打开不同 SVG，而草稿是**按文档**存这一份未保存改动。
 * 编辑 A 没点确定就关了、再打开 B 时，状态栏会提示"发现未保存草稿 · 恢复"，点一下就**把 A 的内容塞进 B**。
 */
export function isEmbedSession(search: string, win: { parent: unknown } = window): boolean {
  return readEmbedOptions(search).enabled && isEmbedded(win);
}

/* ------------------------------------------------------------------ 窗口接线 */

export interface AttachEmbedOptions {
  /** 收/发消息所在的窗口（默认 `window`；测试注入假窗口） */
  win?: Window;
  /** 不校验宿主来源（`?anyOrigin=1`） */
  anyOrigin?: boolean;
  /** 发消息的目标 origin（默认 = 编辑器自身 origin，同源部署即正确） */
  targetOrigin?: string;
}

export interface AttachedEmbed {
  bridge: EmbedBridge;
  /** 摘掉 message 监听（组件卸载时调用） */
  detach(): void;
}

/** 把纯逻辑接到真实窗口上：只认 `event.source === window.parent` 的消息 */
export function attachEmbed(deps: EmbedEditorDeps, options: AttachEmbedOptions = {}): AttachedEmbed {
  const win = options.win ?? window;
  const targetOrigin = options.targetOrigin ?? win.location.origin;
  const bridge = createEmbedBridge(
    {
      ...deps,
      post: (message, origin) => win.parent.postMessage(message, origin),
      origin: () => win.location.origin,
    },
    { targetOrigin, anyOrigin: options.anyOrigin },
  );

  const onMessage = (event: MessageEvent): void => {
    if (event.source !== win.parent) return; // 只收宿主窗口发来的（防同页其它 iframe 串台）
    bridge.handleMessage(event.data, event.origin);
  };

  win.addEventListener('message', onMessage);
  return {
    bridge,
    detach: () => win.removeEventListener('message', onMessage),
  };
}
